import { pluralize } from '@givebutter/util';
import { faGripDotsVertical, faPeopleGroup } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DropTarget } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/constants';
import { useTableSeatingStore } from 'assets/js/pages/campaign-settings/campaign-event/table-seating/store';

const MultiSelectionOverlay = () => {
    const activeData = useTableSeatingStore(state => state.dragState.activeData);

    if (!activeData?.ticketIds) return null;

    const count = activeData.ticketIds.length;

    return (
        <div className="pointer-events-none opacity-90">
            <div className="flex items-center gap-3 rounded-lg border-2 border-blue-400 bg-blue-50 p-3 shadow-lg">
                <FontAwesomeIcon
                    icon={faGripDotsVertical}
                    className="text-blue-600"
                    aria-hidden="true"
                />
                <div className="flex flex-col">
                    <p className="text-sm font-medium text-blue-900">
                        {count} {pluralize(count, 'guest')} selected
                    </p>
                    <p className="text-xs text-blue-700">Drop to move all selected items</p>
                </div>
            </div>
        </div>
    );
};

const GroupOverlay = () => {
    const { activeData, ticketGroups } = useTableSeatingStore(state => ({
        activeData: state.dragState.activeData,
        ticketGroups: state.ticketGroups,
    }));

    if (!activeData?.groupId) return null;

    const ticketGroup = ticketGroups[activeData.groupId];
    const groupTicketCount = activeData.ticketIds?.length || 0;

    if (!ticketGroup || groupTicketCount === 0) return null;

    return (
        <div className="pointer-events-none opacity-90">
            <div className="rounded border-2 border-gray-300 bg-gray-100 px-3 py-2 shadow-lg">
                <div className="flex items-center gap-2">
                    <FontAwesomeIcon
                        icon={faGripDotsVertical}
                        className="text-gray-400"
                        aria-hidden="true"
                    />
                    <span className="text-sm font-medium">{ticketGroup.name}</span>
                    <span className="text-xs text-gray-500">
                        ({groupTicketCount} {pluralize(groupTicketCount, 'guest')})
                    </span>
                </div>
            </div>
        </div>
    );
};

const SingleTicketOverlay = () => {
    const { activeId, activeData, tickets, ticketGroups } = useTableSeatingStore(state => ({
        activeId: state.dragState.activeId,
        activeData: state.dragState.activeData,
        tickets: state.tickets,
        ticketGroups: state.ticketGroups,
    }));

    const ticketId = activeData?.ticketId || activeId;
    if (!ticketId) return null;

    const ticket = tickets[ticketId];
    if (!ticket) return null;

    const ticketGroup = ticket.ticket_group_id ? ticketGroups[ticket.ticket_group_id] : null;

    return (
        <div className="pointer-events-none opacity-90">
            <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
                <FontAwesomeIcon
                    icon={faGripDotsVertical}
                    className="text-gray-400"
                    aria-hidden="true"
                />
                <div className="flex flex-col">
                    <p className="font-medium text-black">
                        <span className="text-sm font-medium">
                            {ticket.first_name} {ticket.last_name}
                        </span>
                    </p>
                    <p className="flex items-center gap-2 text-xs text-gray-600">
                        {ticket.title}
                        {ticketGroup && (
                            <FontAwesomeIcon
                                icon={faPeopleGroup}
                                className="text-brand-violet-primary text-xs"
                                aria-hidden="true"
                            />
                        )}
                    </p>
                </div>
            </div>
        </div>
    );
};

const DragOverlay = () => {
    const dragState = useTableSeatingStore(state => state.dragState);

    if (!dragState.activeId) {
        return null;
    }

    if (dragState.activeData?.type === DropTarget.MULTI_SELECTION) {
        return <MultiSelectionOverlay />;
    }

    if (dragState.activeData?.type === DropTarget.GROUP) {
        return <GroupOverlay />;
    }

    return <SingleTicketOverlay />;
};

export { DragOverlay };
